import { getConfig } from './config';
import { getIdToken } from './auth';

export class GraphQLError extends Error {
  constructor(message: string, readonly errorType?: string, readonly raw?: unknown) {
    super(message);
    this.name = 'GraphQLError';
  }
}

/**
 * Executes a GraphQL operation against AppSync.
 *
 * AppSync returns HTTP 200 with an `errors` array for resolver failures, so the
 * body has to be inspected even on a successful response.
 */
export async function gql<T>(
  query: string,
  variables: Record<string, unknown> = {}
): Promise<T> {
  const cfg = getConfig();
  const token = await getIdToken();

  let res: Response;
  try {
    res = await fetch(cfg.graphqlUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify({ query, variables }),
    });
  } catch (err) {
    throw new GraphQLError('Could not reach the service. Check your connection.', 'NetworkError', err);
  }

  if (!res.ok) {
    throw new GraphQLError(`Service returned ${res.status}.`, 'HttpError', await res.text());
  }

  const body = (await res.json()) as { data?: T; errors?: Array<{ message: string; errorType?: string }> };
  if (body.errors?.length) {
    const first = body.errors[0]!;
    throw new GraphQLError(first.message, first.errorType, body.errors);
  }
  if (!body.data) throw new GraphQLError('Empty response from service.', 'EmptyResponse');
  return body.data;
}
